import { useCallback, useEffect, useState } from 'react';
import { ApiError, NetworkError, api } from '../api';
import type { ReportPeriod } from '../api-modules';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Skeleton, Tabs } from '../components';
import { PeriodPicker, initialPeriod, type Period } from '../components/PeriodPicker';
import { MissedFollowups } from '../components/MissedFollowups';
import { formatMinor } from '../money';
import { IconAlert, IconCalendar, IconClock, IconPatients, IconTooth, IconUsers } from '../icons';

const TILE_ICONS = [IconPatients, IconUsers, IconCalendar, IconAlert, IconClock, IconTooth];
const TILE_TONES = ['teal', 'blue', 'violet', 'amber', 'amber', 'teal'];

/**
 * Reports: new patients and where they came from, each dentist's activity,
 * procedures and missed appointments, over a period.
 *
 * What each person sees is decided by the server, by permission: the clinic's
 * managers see the whole clinic; a dentist sees their own figures only. Money
 * appears only to those who may read the clinic's finance reports; invoices,
 * payments and expenses stay in Finance.
 */
type Tab = 'overview' | 'patients' | 'dentists' | 'procedures' | 'missed';

export function Reports({ permissions }: { permissions: string[] }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('overview');
  const [period, setPeriod] = useState<Period>(() => initialPeriod('month'));
  const [dentistId, setDentistId] = useState('');
  const [dentists, setDentists] = useState<{ membershipId: string; name: string }[]>([]);
  const clinicScope = permissions.includes('reports.read');

  useEffect(() => {
    if (clinicScope) api.appointmentResources().then((r) => setDentists(r.dentists)).catch(() => undefined);
  }, [clinicScope]);

  const p: ReportPeriod = { from: period.from, to: period.to, ...(dentistId ? { dentistId } : {}) };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.insights.title}</h1>
          <p className="page-sub">{clinicScope ? t.insights.sub : t.insights.ownScope}</p>
        </div>
      </div>
      <div className="report-filters">
        <PeriodPicker value={period} onChange={setPeriod} />
        {clinicScope && tab !== 'dentists' ? (
          <select value={dentistId} onChange={(e) => setDentistId(e.target.value)} aria-label={t.insights.dentist}>
            <option value="">{t.insights.allDentists}</option>
            {dentists.map((d) => (
              <option key={d.membershipId} value={d.membershipId}>{d.name}</option>
            ))}
          </select>
        ) : null}
      </div>
      <Tabs
        value={tab}
        onChange={(v) => setTab(v as Tab)}
        tabs={(['overview', 'patients', 'dentists', 'procedures', 'missed'] as const).map((k) => ({ value: k, label: t.insights.tabs[k] }))}
      />
      {tab === 'overview' ? <Overview p={p} /> : null}
      {tab === 'patients' ? <NewPatients p={p} /> : null}
      {tab === 'dentists' ? <DentistsReport p={{ from: p.from, to: p.to }} /> : null}
      {tab === 'procedures' ? <Procedures p={p} /> : null}
      {tab === 'missed' ? <Missed p={p} canWrite={permissions.includes('appointment.write')} /> : null}
      {permissions.includes('finance.reports.read') ? <p className="muted" style={{ marginTop: 16 }}>{t.insights.financeLink}</p> : null}
    </>
  );
}

/** Loads one report for the period; shows loading and errors the same way everywhere. */
function useReport<T>(load: () => Promise<T>, deps: unknown[]) {
  const { t } = useI18n();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(() => {
    setData(null);
    setError(null);
    load()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof NetworkError ? t.errors.network : e instanceof ApiError ? e.message : t.common.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(run, [run]);
  return { data, error, reload: run };
}

/** A table as a CSV file, with a byte-order mark so Excel opens Arabic correctly. */
function downloadCsv(name: string, header: string[], rows: (string | number | null)[][]) {
  const cell = (v: string | number | null) => {
    const s = v === null ? '' : String(v);
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = '﻿' + [header, ...rows].map((r) => r.map(cell).join(',')).join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type: 'text/csv;charset=utf-8' }));
  a.download = `${name}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

function Overview({ p }: { p: ReportPeriod }) {
  const { t } = useI18n();
  const { data, error } = useReport(() => Promise.all([api.reportOverview(p), api.reportPatients(p)]), [p.from, p.to, p.dentistId]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={4} /></div>;
  const [o, pts] = data;
  // Western digits in both languages, as everywhere else in the application.
  const n = (v: number) => new Intl.NumberFormat('en-GB').format(v);
  const tiles: [string, string, string?][] = [
    [t.insights.newPatients, n(o.newPatients)],
    [t.insights.patientsSeen, n(o.patientsSeen)],
    [t.insights.completed, n(o.appointmentsCompleted)],
    [t.insights.noShows, n(o.noShows), o.noShowRate !== null ? `${o.noShowRate}%` : undefined],
    [t.insights.cancelled, n(o.cancelled)],
    [t.insights.procedures, n(o.proceduresCompleted)],
  ];
  return (
    <div className="stack">
      <div className="stats">
        {tiles.map(([label, value, extra], i) => {
          const Icon = TILE_ICONS[i]!;
          return (
            <div key={label} className="card stat">
              <div className="stat-top">
                <span>{label}</span>
                <span className={`stat-icon ${TILE_TONES[i]}`} aria-hidden="true">
                  <Icon size={18} />
                </span>
              </div>
              <div className="stat-value">
                <bdi dir="ltr">{value}</bdi>
              </div>
              {extra ? <div className="stat-hint" title={t.insights.rateHint}>{t.insights.noShowRate}: {extra}</div> : null}
            </div>
          );
        })}
      </div>
      <SourceBars data={pts} />
    </div>
  );
}

function SourceBars({ data }: { data: Awaited<ReturnType<typeof api.reportPatients>> }) {
  const { t, locale } = useI18n();
  const max = Math.max(1, ...data.bySource.map((s) => s.count), ...data.byReferrer.map((s) => s.count));
  const label = (s: { nameEn: string | null; nameAr: string | null }) => (s.nameEn ? (locale === 'ar' && s.nameAr ? s.nameAr : s.nameEn) : t.insights.notRecorded);
  return (
    <div className="split">
      <div className="card">
        <div className="card-head"><h2>{t.insights.bySource}</h2></div>
        <div className="card-body">
          {data.bySource.length === 0 ? <p className="muted">{t.insights.empty}</p> : null}
          {data.bySource.map((s) => (
            <div key={s.sourceId ?? 'none'} className="bar-row">
              <span className="bar-label">{label(s)}</span>
              <span className="bar"><span style={{ width: `${(s.count / max) * 100}%` }} /></span>
              <span className="bar-n">{s.count}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="card-head"><h2>{t.insights.byReferrer}</h2></div>
        <div className="card-body">
          {data.byReferrer.length === 0 ? <p className="muted">{t.insights.empty}</p> : null}
          {data.byReferrer.map((s) => (
            <div key={s.membershipId} className="bar-row">
              <span className="bar-label">{s.name}</span>
              <span className="bar"><span style={{ width: `${(s.count / max) * 100}%` }} /></span>
              <span className="bar-n">{s.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewPatients({ p }: { p: ReportPeriod }) {
  const { t, locale, formatDate, personName } = useI18n();
  const { data, error } = useReport(() => api.reportPatients(p), [p.from, p.to, p.dentistId]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={6} /></div>;
  const src = (r: (typeof data.patients)[number]) =>
    r.source ? (locale === 'ar' && r.source.ar ? r.source.ar : (r.source.en ?? '')) : '';
  const rows = data.patients.map((r) => [r.patientNumber, personName(r), formatDate(r.createdAt), src(r) || t.insights.notRecorded, r.referredBy ?? '', r.sourceDetail ?? '']);
  return (
    <div className="stack">
      <SourceBars data={data} />
      <div className="card">
        <div className="card-head">
          <h2>{t.insights.list} <span className="muted">({data.total})</span></h2>
          {rows.length ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => downloadCsv(`new-patients-${p.from}-${p.to}`, ['#', t.patients.name, t.insights.registered, t.sources.label, t.sources.referredBy, t.sources.detail], rows)}>
              {t.insights.exportCsv}
            </button>
          ) : null}
        </div>
        {rows.length === 0 ? (
          <EmptyState title={t.insights.empty} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>{t.patients.name}</th>
                <th>{t.insights.registered}</th>
                <th>{t.sources.label}</th>
                <th>{t.sources.referredBy}</th>
                <th>{t.sources.detail}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={data.patients[i]!.id}>
                  {r.map((c, j) => (
                    <td key={j} className={j === 0 ? 'ltr-value' : ''}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function DentistsReport({ p }: { p: ReportPeriod }) {
  const { t, locale } = useI18n();
  const { data, error } = useReport(() => api.reportDentists(p), [p.from, p.to]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={5} /></div>;
  const money = data.currency;
  const header = [t.insights.dentist, t.insights.patientsSeen, t.insights.completed, t.insights.noShows, t.insights.noShowRate, t.insights.cancelled, t.insights.referred, t.insights.procedures, ...(money ? [t.insights.value] : [])];
  const rows = data.dentists.map((d) => [
    d.name,
    d.patientsSeen,
    d.appointmentsCompleted,
    d.noShows,
    d.noShowRate === null ? '—' : `${d.noShowRate}%`,
    d.cancelled,
    d.patientsReferred,
    d.proceduresCompleted,
    ...(money ? [formatMinor(d.proceduresValueMinor ?? 0, money, locale)] : []),
  ]);
  return (
    <div className="card">
      <div className="card-head">
        <h2>{t.insights.tabs.dentists}</h2>
        {rows.length ? (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => downloadCsv(`dentists-${p.from}-${p.to}`, header, rows)}>{t.insights.exportCsv}</button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <EmptyState title={t.insights.empty} />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr>{header.map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={data.dentists[i]!.membershipId}>
                  {r.map((c, j) => <td key={j} className={j === 0 ? '' : 'num'}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="muted" style={{ padding: '0 20px 14px', fontSize: 12.5 }}>{t.insights.rateHint}</p>
    </div>
  );
}

function Procedures({ p }: { p: ReportPeriod }) {
  const { t, locale } = useI18n();
  const { data, error } = useReport(() => Promise.all([api.reportProcedures(p), api.reportDentists({ from: p.from, to: p.to })]), [p.from, p.to, p.dentistId]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={6} /></div>;
  const [r, d] = data;
  const money = d.currency;
  const header = [t.insights.procedure, t.insights.count, t.insights.patientsCol, ...(money ? [t.insights.value] : [])];
  const rows = r.procedures.map((x) => [
    `${locale === 'ar' && x.nameAr ? x.nameAr : x.nameEn}${x.code ? ` (${x.code})` : ''}`,
    x.count,
    x.patients,
    ...(money ? [formatMinor(x.valueMinor ?? 0, money, locale)] : []),
  ]);
  return (
    <div className="card">
      <div className="card-head">
        <h2>{t.insights.tabs.procedures}</h2>
        {rows.length ? (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => downloadCsv(`procedures-${p.from}-${p.to}`, header, rows)}>{t.insights.exportCsv}</button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <EmptyState title={t.insights.empty} />
      ) : (
        <table className="table">
          <thead><tr>{header.map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>{row.map((c, j) => <td key={j} className={j === 0 ? '' : 'num'}>{c}</td>)}</tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Missed({ p, canWrite }: { p: ReportPeriod; canWrite: boolean }) {
  const { t, formatDate, personName } = useI18n();
  const { data, error, reload } = useReport(() => api.reportMissed(p), [p.from, p.to, p.dentistId]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <div className="card"><Skeleton rows={6} /></div>;
  const rows = data.missed.map((m) => [
    formatDate(m.startsAt),
    m.patient ? personName(m.patient) : (m.requestedBy?.name ?? ''),
    m.patient?.phone ?? m.requestedBy?.phone ?? '',
    m.dentist.name,
    m.nextBooked ? t.missed.status.rebooked : m.followup ? (t.missed.status as Record<string, string>)[m.followup.status]! : t.insights.notFollowed,
  ]);
  return (
    <div className="stack">
      {rows.length ? (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => downloadCsv(`missed-${p.from}-${p.to}`, [t.insights.registered, t.patients.name, t.patients.phone, t.insights.dentist, t.insights.followup], rows)}>
            {t.insights.exportCsv}
          </button>
        </div>
      ) : null}
      <MissedFollowups rows={data.missed} canWrite={canWrite} onChanged={reload} />
    </div>
  );
}
